'use client'

import {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
  startTransition,
} from 'react'
import {
  MagnifyingGlassIcon,
  XIcon,
  UserCircleIcon,
  PlusIcon,
} from '@phosphor-icons/react'
import { AddPersonDialog } from './add-person-dialog'
import { Button } from '@/ui/button'
import {
  usePrefetchPersonEntries,
  type PersonWithLastInteraction,
} from './select-people'
import { cn } from '@/lib/tailwind'
import { CATEGORY_FILTERS, type PersonCategory } from './category-colors'
import { PersonRow } from './PersonRow'
import { PersonDetailPanel } from './PersonDetailPanel'
import { EmptyState } from './EmptyState'

interface PeoplePageClientProps {
  initialPeople: PersonWithLastInteraction[]
}

export function PeoplePageClient({ initialPeople }: PeoplePageClientProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<PersonCategory | 'all'>(
    'all',
  )
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null)
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)
  const prefetchPersonEntries = usePrefetchPersonEntries()
  const listRef = useRef<HTMLDivElement>(null)

  // Filter people based on search and category
  const filteredPeople = useMemo(() => {
    let result = initialPeople

    if (categoryFilter !== 'all') {
      result = result.filter((p) => p.metadata.category === categoryFilter)
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      result = result.filter((p) => p.title.toLowerCase().includes(q))
    }

    return result
  }, [initialPeople, searchQuery, categoryFilter])

  const selectedPerson = useMemo(
    () => initialPeople.find((p) => p.id === selectedPersonId),
    [initialPeople, selectedPersonId],
  )

  // Keyboard navigation for person list
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (filteredPeople.length === 0) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setFocusedIndex((prev) =>
            prev < filteredPeople.length - 1 ? prev + 1 : prev,
          )
          break
        case 'ArrowUp':
          e.preventDefault()
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : prev))
          break
        case 'Enter':
        case ' ':
          e.preventDefault()
          if (focusedIndex >= 0 && focusedIndex < filteredPeople.length) {
            setSelectedPersonId(filteredPeople[focusedIndex].id)
          }
          break
        case 'Escape':
          e.preventDefault()
          setSelectedPersonId(null)
          break
      }
    },
    [filteredPeople, focusedIndex],
  )

  // Reset focus index when filter changes
  useEffect(() => {
    startTransition(() => {
      setFocusedIndex(-1)
    })
  }, [categoryFilter, searchQuery])

  // Stable callbacks to prevent child re-renders
  const handleSelectPerson = useCallback((id: string) => {
    setSelectedPersonId(id)
  }, [])

  const handleCloseDetail = useCallback(() => {
    setSelectedPersonId(null)
  }, [])

  return (
    <div className="space-y-5 sm:space-y-6">
      {/* Header row */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight sm:text-xl">
            People
          </h1>
          <p className="text-muted-foreground mt-0.5 text-sm sm:text-xs">
            {initialPeople.length} contact
            {initialPeople.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Search and Add */}
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <div className="relative flex-1 sm:flex-initial">
            <MagnifyingGlassIcon
              className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 sm:left-2.5 sm:h-3.5 sm:w-3.5"
              aria-hidden="true"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search people..."
              className="placeholder:text-muted-foreground border-input bg-background focus:ring-ring h-11 w-full rounded-lg border py-1 pr-8 pl-10 text-base transition-colors outline-none focus:ring-2 sm:h-8 sm:w-40 sm:rounded-md sm:pr-7 sm:pl-8 sm:text-xs"
              aria-label="Search people"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="text-muted-foreground hover:text-foreground focus-visible:ring-ring absolute top-1/2 right-2.5 -translate-y-1/2 rounded focus-visible:ring-2 focus-visible:outline-none"
                aria-label="Clear search"
              >
                <XIcon className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
              </button>
            )}
          </div>
          <AddPersonDialog
            trigger={
              <Button size="sm" className="h-11 gap-1.5 px-3 sm:h-8 sm:px-2.5">
                <PlusIcon className="h-4 w-4 sm:h-3.5 sm:w-3.5" weight="bold" />
                <span className="sm:sr-only">Add</span>
              </Button>
            }
          />
        </div>
      </div>

      {/* Category filters */}
      <div
        className="flex gap-1 border-b pb-3 sm:pb-2"
        role="radiogroup"
        aria-label="Filter by category"
      >
        {CATEGORY_FILTERS.map((filter) => (
          <button
            key={filter.value}
            type="button"
            role="radio"
            aria-checked={categoryFilter === filter.value}
            onClick={() => setCategoryFilter(filter.value)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors sm:px-2.5 sm:py-1 sm:text-xs',
              'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
              categoryFilter === filter.value
                ? 'bg-foreground/10 text-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-accent/50',
            )}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {/* Main content: People list + Detail panel */}
      <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
        {/* People list */}
        <div
          ref={listRef}
          className={cn(
            'flex-1 lg:max-w-sm',
            selectedPersonId && 'hidden lg:block',
          )}
          onKeyDown={handleKeyDown}
          role="listbox"
          aria-label="People list"
          aria-activedescendant={
            focusedIndex >= 0
              ? `person-${filteredPeople[focusedIndex]?.id}`
              : undefined
          }
          tabIndex={filteredPeople.length > 0 ? 0 : -1}
        >
          {filteredPeople.length === 0 ? (
            <EmptyState searchQuery={searchQuery} />
          ) : (
            <div className="divide-y">
              {filteredPeople.map((person, index) => (
                <PersonRow
                  key={person.id}
                  person={person}
                  isSelected={selectedPersonId === person.id}
                  isFocused={focusedIndex === index}
                  onSelect={handleSelectPerson}
                  onPrefetch={prefetchPersonEntries}
                />
              ))}
            </div>
          )}
        </div>

        {/* Detail panel */}
        {selectedPersonId && selectedPerson ? (
          <PersonDetailPanel
            person={selectedPerson}
            onClose={handleCloseDetail}
          />
        ) : (
          <div className="hidden flex-1 lg:flex lg:items-center lg:justify-center">
            <div className="text-muted-foreground text-center">
              <UserCircleIcon className="mx-auto h-12 w-12 opacity-30" />
              <p className="mt-3 text-sm">
                Select a person to view their entries
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
